<template>
  <div id="pango-root"></div>
</template>

<script setup>
import { onBeforeUnmount, onMounted } from 'vue'
import throttle from 'lodash/throttle'
import createWidgetClient from 'clients/curriculum_widget_client'
import handleNavigateToEvent from 'clients/curriculum_widget_navigate_handler'
import { useCurrentCustomerStore } from 'vue_features/shared/store/composables'
import { useCurrentUserStore } from 'vue_features/shared/store/composables'
import debug from 'debug'

const { state: userState } = useCurrentUserStore()
const { state: customerState } = useCurrentCustomerStore()
const log = debug('app:vue_features/curriculum_widget/components/curriculum_widget')

const props = defineProps({
  scriptUrl: {
    type: String,
    required: true,
  },
  grade: {
    type: String,
    default: null,
  },
  unit: {
    type: String,
    default: null,
  },
  section: {
    type: String,
    default: null,
  },
  lesson: {
    type: String,
    default: null,
  },
  standards: {
    type: Array,
    default: () => [],
  },
  currentCurricula: {
    type: Array,
    default: () => [],
  },
  availableCurricula: {
    type: Array,
    default: () => [],
  },
  curriculumCustomerId: {
    type: Number,
    default: null,
  }
})

onMounted(() => {
  window.CurriculumWidgetClient = createWidgetClient({ scriptUrl: props.scriptUrl })
  const ctx = {
    customerSubdomain: customerState.subdomain,
    userId: userState.id,
    grade: props.grade,
    unit: props.unit,
    section: props.section,
    lesson: props.lesson,
    standards: props.standards,
    currentCurricula: props.currentCurricula,
    availableCurricula: props.availableCurricula,
    curriculumCustomerId: props.curriculumCustomerId,
  }
  window.CurriculumWidgetClient.init(ctx).then(() => {
    log('Successfully initialized ClassMate widget with', ctx)

    if (window.PangoWidget && window.PangoWidget.loadContext) {
      window.PangoWidget.loadContext(ctx)
      log('PangoWidget context loaded')
    } else {
      log('PangoWidget not found or loadContext not available')
    }

    // Mobile fix: move widget icon above bottom toolbar
    adjustWidgetPositionForMobile()

    window.CurriculumWidgetClient.on('navigateTo', (event) => {
      handleNavigateToEvent(event.payload).catch((error) => {
        log('Failed to handle ClassMate navigateTo event', event, error)
      })
    })
  }).catch((error) => {
    log('Failed to initialize ClassMate widget', ctx, error)
  })

})

const MOBILE_BREAKPOINT = 1200
const MOBILE_BOTTOM_OFFSET = '145px'
const DEFAULT_BOTTOM_OFFSET = '10px'
const MAX_ADJUST_ATTEMPTS = 10
const ADJUST_RETRY_MS = 500

let resizeHandler = null
let timeoutHandle = 0

const adjustWidgetPositionForMobile = () => {
  const tryAdjust = (attempts = 0) => {
    // Shadow root is on a child element with data-pango-shadow-host="true"
    const pangoRoot = document.getElementById('pango-root')
    const shadowHost = pangoRoot?.querySelector('[data-pango-shadow-host="true"]')
    if (!shadowHost?.shadowRoot) {
      if (attempts < MAX_ADJUST_ATTEMPTS) {
        timeoutHandle = setTimeout(() => tryAdjust(attempts + 1), ADJUST_RETRY_MS)
      }
      return
    }

    const portalRoot = shadowHost.shadowRoot.getElementById('pango-widget-portal-root')
    if (!portalRoot) {
      if (attempts < MAX_ADJUST_ATTEMPTS) {
        timeoutHandle = setTimeout(() => tryAdjust(attempts + 1), ADJUST_RETRY_MS)
      }
      return
    }

    // Widget button is a direct child of portal root
    const widgetButton = portalRoot.querySelector('.gpt-widget__closed')

    if (widgetButton) {
      const updatePosition = () => {
        const bottom = window.innerWidth <= MOBILE_BREAKPOINT ? MOBILE_BOTTOM_OFFSET : DEFAULT_BOTTOM_OFFSET
        widgetButton.style.setProperty('bottom', bottom, 'important')
      }

      updatePosition()
      log('Adjusted widget position for screen width:', window.innerWidth)

      // Set up resize listener if not already set
      if (!resizeHandler) {
        resizeHandler = throttle(updatePosition, 150)
        window.addEventListener('resize', resizeHandler)
      }
    } else if (attempts < MAX_ADJUST_ATTEMPTS) {
      timeoutHandle = setTimeout(() => tryAdjust(attempts + 1), ADJUST_RETRY_MS)
    }
  }

  tryAdjust()
}

onBeforeUnmount(() => {
  clearTimeout(timeoutHandle)
  if (resizeHandler) {
    window.removeEventListener('resize', resizeHandler)
    resizeHandler = null
  }
  if (window.CurriculumWidgetClient) {
    window.CurriculumWidgetClient.destroy()
    delete window.CurriculumWidgetClient
  }
})
</script>
